import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import type { WatermarkEffective } from '../../api'
import { t } from '../../i18n'
import { Toast } from '../../ui'
import { useSession } from '../../session'
import { BILLING_PATH, hasPlan } from '../../billing'
import { markStep, markToastText, type MarkMemo, type MarkToastKind } from '../watermark'

/*
 * WM3 (WMU-05, ADR-0005 W7): the plan changed on air and the Strevix mark came on or went off at once.
 * The Studio compares the previous and the new watermark_effective of the SSE stream object; the first
 * snapshot after the page loads only becomes the memory. A toast of the system (ToastsAndBanners:
 * bottom-right, full width on phones, info / success, 4 s, role="status") — not a banner and not the
 * broadcast strip: nothing to do to keep the broadcast going. Each change also re-reads the outputs:
 * their transcode_effective.watermark says which rows carry the mark line (WMU-03).
 */
export function MarkToast({ eff }: { eff: WatermarkEffective | undefined }) {
  const { user } = useSession()
  const qc = useQueryClient()
  const memo = useRef<MarkMemo | undefined>(undefined)
  const seen = useRef<string>('')
  const [kind, setKind] = useState<MarkToastKind | null>(null)
  const key = eff ? `${eff.on}|${eff.reason}|${eff.detail}|${eff.since}` : ''

  useEffect(() => {
    if (!eff || key === seen.current) return
    const first = seen.current === ''
    seen.current = key
    const step = markStep(memo.current, eff)
    memo.current = step.memo
    if (first) return
    void qc.invalidateQueries({ queryKey: ['destinations'] })
    if (step.toast) setKind(step.toast)
    // the key carries every field of eff
  }, [key, qc])

  useEffect(() => {
    if (!kind) return
    const id = window.setTimeout(() => setKind(null), 4000)
    return () => window.clearTimeout(id)
  }, [kind])

  if (!kind) return null
  const text = markToastText(kind, user?.plan?.name)
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[70] flex justify-end max-md:inset-x-0 max-md:bottom-0">
      <div className="pointer-events-auto w-full max-w-[360px] max-md:max-w-none [&_.sx-toast]:w-full" data-testid={`wm-toast-${kind}`}>
        <Toast state={kind === 'on' ? 'idle' : 'live'} title={text.title} body={text.body} closeLabel={t('common.dismiss')} onClose={() => setKind(null)}
          action={hasPlan(user) ? <Link to={BILLING_PATH} data-testid="wm-toast-plan" className="sx-btn sm no-underline max-md:min-h-[44px]">{t('plan.seePlan')}</Link> : undefined} />
      </div>
    </div>
  )
}
